import { Link } from 'react-router-dom'
import { ArrowRight, CalendarDays, GraduationCap } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { useGetAcademyProgressQuery } from '@/redux/collaborationsApi'
import { POSTING_GOAL } from '@/lib/athleteScore'
import { fmtDuration, isNew, nextLesson, trackByKey } from '@adthlete/common/utils/academy'
import { useAcademyCatalogue } from '@/hooks/useAcademyCatalogue'

/**
 * The two places an athlete should go most, at the top of Home: their content calendar and the
 * next Creator Academy lesson. Neither has a tab in the bottom bar, so Home sends them there.
 */
const HomeCtas = () => {
  const { athlete } = useAuth()
  const { data: academy } = useGetAcademyProgressQuery()
  const cat = useAcademyCatalogue()
  const posting = (athlete as any)?.posting
  const goal = posting?.goal ?? POSTING_GOAL
  const thisWeek = posting?.thisWeek ?? 0
  const behind = thisWeek < goal

  const progress = academy?.progress ?? {}
  const lesson = nextLesson(cat, progress)
  const started = (progress[lesson?.id ?? '']?.position ?? 0) > 0
  const newCount = cat.lessons.filter((l) => isNew(l) && !progress[l.id]?.completed).length
  // Until the first lesson is published, the card says they're coming
  const comingSoon = !lesson

  const card =
    'flex min-w-0 flex-col rounded-2xl border border-border bg-card p-4 shadow-sm transition-colors hover:bg-muted/40'
  const eyebrow =
    'flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground'
  const cta = 'inline-flex items-center gap-1 text-sm font-semibold text-[#3F7A0A]'

  return (
    <div className="mb-5 grid grid-cols-2 gap-3">
      {/* Short of this week's goal: open the calendar straight into planning a post */}
      <Link to={behind ? '/calendar?plan=1' : '/calendar'} className={card}>
        <span className={eyebrow}>
          <CalendarDays className="h-3.5 w-3.5 text-[#DB2777]" /> Calendar
        </span>
        <span className="mt-2 text-base font-bold leading-snug text-foreground">
          {thisWeek} of {goal} posts this week
        </span>
        <span className="mt-0.5 text-sm text-muted-foreground">
          {behind
            ? `Plan ${goal - thisWeek} more with shoot and edit days`
            : 'Goal hit. Plan next week'}
        </span>
        <span className={`${cta} mt-auto pt-3`}>
          {behind ? 'Plan posts' : 'Open calendar'} <ArrowRight className="h-4 w-4" />
        </span>
      </Link>

      {comingSoon || !lesson ? (
        <Link to="/academy" className={card}>
          <span className={eyebrow}>
            <GraduationCap className="h-3.5 w-3.5 text-[#6D28D9]" />
            Academy
          </span>
          <span className="mt-2 text-base font-bold leading-snug text-foreground">
            First lessons coming soon
          </span>
          <span className="mt-0.5 text-sm text-muted-foreground">
            Short how-tos on the skills brands pay for
          </span>
          <span className={`${cta} mt-auto pt-3`}>
            Preview <ArrowRight className="h-4 w-4" />
          </span>
        </Link>
      ) : (
        <Link to={`/academy?v=${lesson.id}`} className={card}>
          <span className={eyebrow}>
            <GraduationCap
              className="h-3.5 w-3.5"
              style={{ color: trackByKey(cat, lesson.track)?.ink }}
            />
            Academy
          </span>
          <span className="mt-2 line-clamp-2 text-base font-bold leading-snug text-foreground">
            {lesson.title}
          </span>
          <span className="mt-0.5 text-sm text-muted-foreground">
            {started ? 'Pick up where you left off' : 'Up next'} ·{' '}
            {fmtDuration(lesson.durationSec)}
          </span>
          {newCount ? (
            <span className="mt-1.5 w-fit whitespace-nowrap rounded-full bg-[#C0FD72] px-2 py-0.5 text-xs font-bold text-[#0F172A]">
              {newCount} new {newCount === 1 ? 'lesson' : 'lessons'}
            </span>
          ) : null}
          <span className={`${cta} mt-auto pt-3`}>
            {started ? 'Keep watching' : 'Watch'} <ArrowRight className="h-4 w-4" />
          </span>
        </Link>
      )}
    </div>
  )
}

export default HomeCtas
